import React from 'react';
import { useQuery } from '@apollo/client';
import { Close } from '@mui/icons-material';
import { lightGreen } from '@mui/material/colors';
import { DictionaryItem, QueryListResponse, SelectOption } from 'global';
import { Button, IconButton, Popover, Stack, Typography } from '@mui/material';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';

import { calendarApi, useCalendar } from '../../../api';
import { DealActivityAPINameSpace } from '../../../../api/types';

interface Props {
  onClose: VoidFunction;
  anchorEl: HTMLElement | null;
}

export const OtherCalendarsSettings = ({ onClose, anchorEl }: Props) => {
  const { handleAddCalendarView, isAddingCalendarView } = useCalendar();
  const { data, loading, previousData, error } = useQuery<DealActivityAPINameSpace<QueryListResponse<DictionaryItem>>>(
    calendarApi.sharing.queries.viewableUsers
  );

  const users = (data ?? previousData)?.dealActivity?.result?.list ?? [];

  const handleAddUserCalendar = async (user: SelectOption) => {
    onClose();

    await handleAddCalendarView({ userId: user.value, name: user.label, color: lightGreen[300] }, api => ({
      awaitRefetchQueries: true,
      refetchQueries: [api.sharing.queries.configuration, api.events.queries.listWithActivities],
    }));
  };

  return (
    <Popover
      onClose={onClose}
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Stack padding="0.5rem" sx={{ minWidth: 280, maxWidth: 320 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2" color="text.secondary">
            Add Calendars
          </Typography>

          <IconButton loading={isAddingCalendarView} size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        <RenderWithFallback isLoading={loading} error={error}>
          <Stack gap="0.5rem" sx={{ maxHeight: 280, overflowY: 'auto' }}>
            {users.map(user => (
              <Stack key={user.value} direction="row" alignItems="center" justifyContent="space-between" gap="0.5rem">
                <Stack direction="row" alignItems="center" gap="0.5rem" sx={{ overflow: 'hidden' }}>
                  <Typography variant="body2" noWrap>
                    {user.label}
                  </Typography>
                </Stack>

                <Button size="small" variant="text" onClick={() => handleAddUserCalendar(user)}>
                  Add
                </Button>
              </Stack>
            ))}

            {!users.length && (
              <Typography variant="body2" color="text.secondary">
                No users found
              </Typography>
            )}
          </Stack>
        </RenderWithFallback>
      </Stack>
    </Popover>
  );
};
